/**
 * Cron Job Service
 */

import { queryClass, createObject, updateObject, getObjectById, getCurrentParseConfig } from '@/services/adapters/parse/parse-client'
import {
  CronJob,
  CronJobFormData,
  CronJobStats,
  CronJobQueryParams,
  CronJobType,
  CronJobStatus,
} from './cron-job.types'

const CLASS_NAME = 'CronJob'

function mapToCronJob(raw: any): CronJob {
  return {
    objectId: raw.objectId,
    name: raw.name || '',
    description: raw.description || '',
    type: raw.type || CronJobType.LOSSBACK,
    schedule: raw.schedule || '0 0 * * *',
    is_active: raw.is_active ?? false,
    last_run_at: raw.last_run_at?.iso || raw.last_run_at,
    last_status: raw.last_status,
    last_error: raw.last_error,
    last_duration: raw.last_duration,
    next_run_at: raw.next_run_at?.iso || raw.next_run_at,
    run_count: raw.run_count || 0,
    config: raw.config || {},
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
  }
}

export class CronJobService {
  /**
   * ดึงรายการ Cron Jobs
   */
  static async find(
    sessionToken: string,
    params: CronJobQueryParams = {}
  ): Promise<{ items: CronJob[]; total: number }> {
    const { query, type, is_active, page = 1, limit = 50 } = params

    const where: Record<string, any> = {}

    // Filter by type
    if (type) {
      where.type = type
    }

    // Filter by active status
    if (typeof is_active === 'boolean') {
      where.is_active = is_active
    }

    // Search by name
    if (query) {
      where.name = { $regex: query, $options: 'i' }
    }

    const result = await queryClass(
      CLASS_NAME,
      {
        where,
        order: '-updatedAt',
        limit,
        skip: (page - 1) * limit,
      },
      sessionToken
    )

    return {
      items: (result.results || []).map(mapToCronJob),
      total: result.count || 0,
    }
  }

  /**
   * ดึง Cron Job ตาม ID
   */
  static async getById(_sessionToken: string, id: string): Promise<CronJob | null> {
    try {
      const result = await getObjectById(CLASS_NAME, id)
      return result ? mapToCronJob(result) : null
    } catch {
      return null
    }
  }

  /**
   * สร้าง Cron Job ใหม่
   */
  static async create(_sessionToken: string, data: CronJobFormData): Promise<CronJob> {
    const result = await createObject(
      CLASS_NAME,
      {
        ...data,
        run_count: 0,
        last_status: CronJobStatus.PENDING,
      }
    )
    return mapToCronJob({ ...data, ...result })
  }

  /**
   * อัพเดท Cron Job
   */
  static async update(_sessionToken: string, id: string, data: Partial<CronJobFormData>): Promise<void> {
    await updateObject(CLASS_NAME, id, data)
  }

  /**
   * ลบ Cron Job (soft delete)
   */
  static async delete(_sessionToken: string, id: string): Promise<void> {
    await updateObject(CLASS_NAME, id, { deletedAt: new Date().toISOString() })
  }

  /**
   * เปิด/ปิด Cron Job
   */
  static async toggleActive(_sessionToken: string, id: string, is_active: boolean): Promise<void> {
    await updateObject(CLASS_NAME, id, { is_active })
  }

  /**
   * ดึงสถิติ
   */
  static async getStats(sessionToken: string): Promise<CronJobStats> {
    const [all, active, running, failed] = await Promise.all([
      queryClass(CLASS_NAME, { limit: 0 }, sessionToken),
      queryClass(CLASS_NAME, { where: { is_active: true }, limit: 0 }, sessionToken),
      queryClass(CLASS_NAME, { where: { last_status: CronJobStatus.RUNNING }, limit: 0 }, sessionToken),
      queryClass(CLASS_NAME, { where: { last_status: CronJobStatus.FAILED }, limit: 0 }, sessionToken),
    ])

    return {
      total: all.count || 0,
      active: active.count || 0,
      inactive: (all.count || 0) - (active.count || 0),
      running: running.count || 0,
      failed: failed.count || 0,
    }
  }

  /**
   * ตรวจสอบชื่อซ้ำ
   */
  static async checkNameExists(sessionToken: string, name: string, excludeId?: string): Promise<boolean> {
    const where: Record<string, any> = { name }
    if (excludeId) {
      where.objectId = { $ne: excludeId }
    }

    const result = await queryClass(CLASS_NAME, { where, limit: 1 }, sessionToken)
    return (result.results?.length || 0) > 0
  }

  /**
   * สร้าง Default Cron Jobs — เรียก Cloud Function createDefaultCronJobs (backend เป็น single source of truth)
   */
  static async createDefaultJobs(sessionToken: string): Promise<{ created: string[]; skipped: string[] }> {
    const config = getCurrentParseConfig()
    const response = await fetch(`${config.serverURL}/functions/createDefaultCronJobs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Parse-Application-Id': config.appId,
        'X-Parse-Session-Token': sessionToken,
      },
      body: JSON.stringify({}),
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      throw new Error(err.error || 'Failed to create default cron jobs')
    }
    const data = await response.json()
    const result = data.result ?? data
    return {
      created: result.created ?? [],
      skipped: result.skipped ?? [],
    }
  }

  /**
   * ลบ CronJob ที่ deprecated (report/notification/cleanup/backup/sync_member_balance) ออกจาก DB
   */
  static async removeDeprecatedCronJobs(sessionToken: string): Promise<{ removed: string[]; count: number }> {
    const config = getCurrentParseConfig()
    const response = await fetch(`${config.serverURL}/functions/removeDeprecatedCronJobs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Parse-Application-Id': config.appId,
        'X-Parse-Session-Token': sessionToken,
      },
      body: JSON.stringify({}),
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      throw new Error(err.error || 'Failed to remove deprecated cron jobs')
    }
    const data = await response.json()
    const result = data.result ?? data
    return {
      removed: result.removed ?? [],
      count: result.count ?? 0,
    }
  }

  /**
   * Test/Run CronJob Now (manual execution)
   */
  static async testCronJob(sessionToken: string, jobName: string): Promise<{
    success: boolean
    status: string
    duration?: number
    result?: any
    error?: string
    message: string
  }> {
    const config = getCurrentParseConfig()
    const response = await fetch(`${config.serverURL}/functions/testCronJob`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Parse-Application-Id': config.appId,
        'X-Parse-Session-Token': sessionToken,
      },
      body: JSON.stringify({ jobName }),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error || 'Failed to test cron job')
    }

    const data = await response.json()
    return data.result || data
  }

  /**
   * Reload Scheduler
   */
  static async reloadScheduler(sessionToken: string): Promise<{ success: boolean; message: string }> {
    const config = getCurrentParseConfig()
    const response = await fetch(`${config.serverURL}/functions/reloadScheduler`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Parse-Application-Id': config.appId,
        'X-Parse-Session-Token': sessionToken,
      },
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error || 'Failed to reload scheduler')
    }

    const data = await response.json()
    return data.result || data
  }

  /**
   * Get CronJob Logs
   */
  static async getCronJobLogs(
    sessionToken: string,
    jobName: string,
    page: number = 1,
    limit: number = 50
  ): Promise<{
    success: boolean
    logs: Array<{
      id: string
      status: string
      started_at?: string
      completed_at?: string
      duration?: number
      error_message?: string
      result?: any
      metadata?: any
      createdAt?: string
    }>
    total: number
    page: number
    limit: number
  }> {
    const config = getCurrentParseConfig()
    const response = await fetch(`${config.serverURL}/functions/getCronJobLogs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Parse-Application-Id': config.appId,
        'X-Parse-Session-Token': sessionToken,
      },
      body: JSON.stringify({ jobName, page, limit }),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error || 'Failed to get cron job logs')
    }

    const data = await response.json()
    return data.result || data
  }

  /**
   * Get CronJob Statistics
   */
  static async getCronJobStats(
    sessionToken: string,
    jobName: string,
    days: number = 30
  ): Promise<{
    success: boolean
    stats: {
      total_runs: number
      success_count: number
      error_count: number
      total_duration: number
      avg_duration: number
      success_rate: number
    }
    period_days: number
  }> {
    const config = getCurrentParseConfig()
    const response = await fetch(`${config.serverURL}/functions/getCronJobStats`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Parse-Application-Id': config.appId,
        'X-Parse-Session-Token': sessionToken,
      },
      body: JSON.stringify({ jobName, days }),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error || 'Failed to get cron job stats')
    }

    const data = await response.json()
    return data.result || data
  }
}

